import { useParams } from "react-router-dom";
import { Button, Typography, Box, Chip, Grid, Paper, IconButton, Divider, Card, CardContent } from "@mui/material";
import { Download, FileDownload, Visibility, Edit, ArrowBack, Block, PowerSettingsNew } from "@mui/icons-material";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { useState } from "react";
import { enqueueSnackbar } from "notistack";

import DashboardContent from "@/components/app/DashboardContent";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import { useGetCouponById, useRevokeCoupon, useDownloadCouponCodes, useToggleCoupon } from "@/api/hooks/useCoupons";
import { IParams } from "@/types/params";
import { getAxiosError } from "@/utils/get-axios-error";
import RevokeModal from "@/components/app/modalVariants/revoke";
import ConfirmationModal from "@/components/app/modalVariants/confirmation";

export default function CouponDetails() {
    const { id } = useParams<IParams>();
    const [showRevokeModal, setShowRevokeModal] = useState(false);
    const [showToggleModal, setShowToggleModal] = useState(false);
    const [showAllCodes, setShowAllCodes] = useState(false);
    const MAX_CODES_TO_SHOW = 100;

    const editAccess = useCheckPermissionAccess(PagesAccess.COUPON, OperationAccess.UPDATE);
    const listAccess = useCheckPermissionAccess(PagesAccess.COUPON, OperationAccess.LIST);
    const revokeAccess = useCheckPermissionAccess(PagesAccess.COUPON, OperationAccess.DELETE); // Using DELETE permission for revoke

    const { data: couponData, isLoading } = useGetCouponById(id ? Number(id) : 0);
    const { mutate: revokeCoupon, isPending: isRevoking } = useRevokeCoupon();
    const { mutate: downloadCodes, isPending: isDownloading } = useDownloadCouponCodes();
    const { mutate: toggleCoupon, isPending: isToggling } = useToggleCoupon();
    const coupon = couponData?.data;

    const displayedCodes = showAllCodes ? coupon?.codes || [] : (coupon?.codes || []).slice(0, MAX_CODES_TO_SHOW);

    const hasMoreCodes = (coupon?.codes?.length || 0) > MAX_CODES_TO_SHOW;

    const getAmountTypeLabel = (amountType: string) => {
        return amountType === "FIXED" ? "Fixed" : "Percentage";
    };

    const getAppliedToLabel = (appliedTo: string) => {
        return appliedTo === "ALL" ? "All" : "First Subscription";
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "ACTIVE":
                return "success";
            case "DE_ACTIVE":
                return "warning";
            case "REVOKED":
                return "error";
            case "MAXED_OUT":
                return "error";
            default:
                return "default";
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case "ACTIVE":
                return "Active";
            case "DE_ACTIVE":
                return "Deactivated";
            case "REVOKED":
                return "Revoked";
            case "MAXED_OUT":
                return "Maxed Out";
            default:
                return status;
        }
    };

    const handleExportCodes = (codes: any[]) => {
        if (!codes || codes.length === 0) {
            enqueueSnackbar("No codes available to export", { variant: "warning" });
            return;
        }

        // Get the group ID from the first code
        const groupId = codes[0]?.groupId;
        if (!groupId) {
            enqueueSnackbar("Group ID not found", { variant: "error" });
            return;
        }

        downloadCodes(groupId, {
            onSuccess: (data) => {
                // Create blob and download
                const blob = new Blob([data], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `coupon-codes-group-${groupId}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                enqueueSnackbar("Codes exported successfully", { variant: "success" });
            },
            onError: (err) => {
                const error = getAxiosError(err);
                const message = error?.meta.message || "Failed to export codes";
                enqueueSnackbar(message, { variant: "error" });
            },
        });
    };

    const handleExportCouponWithDetails = (coupon: any) => {
        const data = {
            id: coupon.id,
            title: coupon.title,
            description: coupon.description,
            amountType: coupon.amountType,
            amountValue: coupon.amountValue,
            appliedTo: coupon.appliedTo,
            status: coupon.status,
            startDate: coupon.startDate,
            expireDate: coupon.expireDate,
            usagePerUser: coupon.usagePerUser,
            usageLimit: coupon.usageLimit,
            minimumPrice: coupon.minimumPrice,
            plans: coupon.plans,
            codes: coupon.codes?.map((code: any) => code.code),
            createdAt: coupon.createdAt,
            updatedAt: coupon.updatedAt,
        };

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `coupon-${coupon.id}-details.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleRevoke = (revokeReason?: string) => {
        if (!id) return;

        revokeCoupon(
            {
                id: Number(id),
                body: { revokeReason },
            },
            {
                onSuccess: () => {
                    setShowRevokeModal(false);
                    enqueueSnackbar("Coupon revoked successfully", { variant: "success" });
                },
                onError: (err: any) => {
                    const error = getAxiosError(err);
                    const message = error?.meta.message || "Failed to revoke coupon";
                    enqueueSnackbar(message, { variant: "error" });
                },
            }
        );
    };

    const handleToggle = () => {
        if (!id) return;

        toggleCoupon(Number(id), {
            onSuccess: () => {
                setShowToggleModal(false);
                enqueueSnackbar("Coupon status toggled successfully", { variant: "success" });
            },
            onError: (err: any) => {
                const error = getAxiosError(err);
                const message = error?.meta.message || "Failed to toggle coupon status";
                enqueueSnackbar(message, { variant: "error" });
            },
        });
    };

    if (isLoading) {
        return (
            <DashboardContent>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Typography variant="h6" color="text.secondary">
                        Loading coupon details...
                    </Typography>
                </Box>
            </DashboardContent>
        );
    }

    if (!coupon) {
        return (
            <DashboardContent>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Typography variant="h6" color="error">
                        Coupon not found
                    </Typography>
                </Box>
            </DashboardContent>
        );
    }

    return (
        <>
            {/* <DashboardHeader
        title={`Coupon #${coupon.id}`}
      >
        <Typography component="p" color="text.secondary">
          View detailed information about this coupon and manage its settings.
        </Typography>
      </DashboardHeader> */}

            <DashboardContent>
                <Box
                    sx={{
                        maxWidth: "1200px",
                        margin: "0 auto",
                        p: { xs: 1, sm: 2 },
                        px: { xs: 1, sm: 2, md: 3 },
                    }}
                >
                    {/* Action Buttons Row */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: { xs: 1, sm: 2 },
                            mb: 3,
                            p: { xs: 1, sm: 2 },
                            borderRadius: 2,
                            justifyContent: "flex-end",
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            onClick={() => handleExportCodes(coupon.codes)}
                            variant="outlined"
                            size="small"
                            startIcon={<Download />}
                            disabled={isDownloading}
                            sx={{
                                color: "success.main",
                                borderColor: "success.main",
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 0.75, sm: 1 },
                                "&:hover": {
                                    backgroundColor: "success.main",
                                    color: "white",
                                    borderColor: "success.main",
                                },
                            }}
                        >
                            {isDownloading ? "Exporting..." : "Export Codes"}
                        </Button>
                        {revokeAccess && coupon.status !== "REVOKED" && coupon.status !== "MAXED_OUT" && (
                            <Button
                                onClick={() => setShowRevokeModal(true)}
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<Block />}
                                sx={{
                                    borderColor: "error.main",
                                    color: "error.main",
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 0.75, sm: 1 },
                                    "&:hover": {
                                        backgroundColor: "error.main",
                                        color: "white",
                                        borderColor: "error.main",
                                    },
                                }}
                            >
                                Revoke Coupon
                            </Button>
                        )}
                        {editAccess && coupon.status !== "REVOKED" && coupon.status !== "MAXED_OUT" && (
                            <Button
                                onClick={() => setShowToggleModal(true)}
                                variant="outlined"
                                color={coupon.status === "ACTIVE" ? "warning" : "success"}
                                size="small"
                                startIcon={<PowerSettingsNew />}
                                disabled={isToggling}
                                sx={{
                                    borderColor: coupon.status === "ACTIVE" ? "warning.main" : "success.main",
                                    color: coupon.status === "ACTIVE" ? "warning.main" : "success.main",
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 0.75, sm: 1 },
                                    "&:hover": {
                                        backgroundColor: coupon.status === "ACTIVE" ? "warning.main" : "success.main",
                                        color: "white",
                                        borderColor: coupon.status === "ACTIVE" ? "warning.main" : "success.main",
                                    },
                                }}
                            >
                                {isToggling
                                    ? "Toggling..."
                                    : `${coupon.status === "ACTIVE" ? "Deactivate" : "Activate"} Coupon`}
                            </Button>
                        )}
                        {editAccess && coupon.canUpdate && coupon.status !== "REVOKED" && coupon.status !== "MAXED_OUT" && (
                            <Button
                                component={Link}
                                to={ROUTE_CONSTANTS.SETTINGS.COUPONS.UPDATE.EDIT_BY_ID(coupon.id).ABSOLUTE}
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<Edit />}
                                sx={{
                                    borderRadius: 2,
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 0.75, sm: 1 },
                                }}
                            >
                                Edit Coupon
                            </Button>
                        )}

                        {listAccess && (
                            <Button
                                component={Link}
                                to={ROUTE_CONSTANTS.SETTINGS.COUPONS.ROOT.ABSOLUTE}
                                variant="outlined"
                                size="small"
                                startIcon={<ArrowBack />}
                                sx={{
                                    borderRadius: 2,
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 0.75, sm: 1 },
                                }}
                            >
                                Back to List
                            </Button>
                        )}
                    </Box>

                    {/* Status Banner */}
                    <Card
                        sx={{
                            mb: 3,
                            background: `linear-gradient(135deg, ${
                                coupon.status === "ACTIVE"
                                    ? "#4caf50"
                                    : coupon.status === "REVOKED"
                                      ? "#f44336"
                                      : coupon.status === "DE_ACTIVE"
                                        ? "#ff9800"
                                        : coupon.status === "MAXED_OUT"
                                          ? "#f44336"
                                          : "#ff9800"
                            }15, ${
                                coupon.status === "ACTIVE"
                                    ? "#4caf50"
                                    : coupon.status === "REVOKED"
                                      ? "#f44336"
                                      : coupon.status === "DE_ACTIVE"
                                        ? "#ff9800"
                                        : coupon.status === "MAXED_OUT"
                                          ? "#f44336"
                                          : "#ff9800"
                            }05)`,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Typography variant="h6" color="text.primary">
                                    {`Coupon #${coupon.id}`}
                                </Typography>
                                <Chip
                                    color={getStatusColor(coupon.status)}
                                    label={getStatusLabel(coupon.status)}
                                    size="medium"
                                    variant="filled"
                                    sx={{ fontWeight: "bold" }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Warning Banner for Used Coupons */}
                    {editAccess && !coupon.canUpdate && (
                        <Card
                            sx={{
                                mb: 3,
                                background: "linear-gradient(135deg, #ff980015, #ff980005)",
                                border: "1px solid #ff9800",
                            }}
                        >
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Typography variant="h6" color="warning.main" sx={{ fontWeight: "bold" }}>
                                        ⚠️ Edit Restriction
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    This coupon cannot be edited because it has been used by customers. You can still revoke
                                    or toggle its status.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {/* Basic Information */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: "100%", boxShadow: 2 }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
                                    >
                                        💰 Basic Information
                                    </Typography>

                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                ID
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                #{coupon.id}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Amount Type
                                            </Typography>
                                            <Chip
                                                label={getAmountTypeLabel(coupon.amountType)}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Amount Value
                                            </Typography>
                                            <Typography variant="h6" color="primary.main" sx={{ fontWeight: "bold" }}>
                                                {coupon.amountValue}
                                                {coupon.amountType === "FIXED" ? " USD" : "%"}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Applied To
                                            </Typography>
                                            <Chip
                                                label={getAppliedToLabel(coupon.appliedTo)}
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Dates */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: "100%", boxShadow: 2 }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
                                    >
                                        📅 Dates & Timeline
                                    </Typography>

                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Start Date
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                {dayjs(coupon.startDate).format("YYYY-MM-DD HH:mm")}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Expire Date
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                {coupon.expireDate
                                                    ? dayjs(coupon.expireDate).format("YYYY-MM-DD HH:mm")
                                                    : "No Expiry"}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Created At
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                {dayjs(coupon.createdAt).format("YYYY-MM-DD HH:mm")}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Updated At
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                {dayjs(coupon.updatedAt).format("YYYY-MM-DD HH:mm")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Usage Limits */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: "100%", boxShadow: 2 }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
                                    >
                                        🔒 Usage Limits
                                    </Typography>

                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Usage Per User
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                {coupon.usagePerUser || "Unlimited"}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Total Usage Limit
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                {coupon.usageLimit || "Unlimited"}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Current Usage
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium", color: "primary.main" }}>
                                                {coupon.usageCount || 0}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Minimum Price
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                {coupon.minimumPrice || "No Minimum"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Plans */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: "100%", boxShadow: 2 }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
                                    >
                                        📋 Selected Plans
                                    </Typography>

                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {coupon.plans && coupon.plans.length > 0 ? (
                                            coupon.plans.map((plan: any, index: number) => (
                                                <Chip
                                                    key={index}
                                                    label={plan.ml_title?.en || plan.ml_title?.ar || `Plan ${plan.id}`}
                                                    variant="outlined"
                                                    size="small"
                                                    color="info"
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No plans selected
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Description */}
                        {coupon.description && (
                            <Grid item xs={12}>
                                <Card sx={{ boxShadow: 2 }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
                                        >
                                            📝 Description
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                lineHeight: 1.6,
                                                p: 2,
                                                backgroundColor: "#f5f5f5",
                                                borderRadius: 2,
                                                border: "1px solid #e0e0e0",
                                                color: "#333",
                                                fontWeight: "medium",
                                            }}
                                        >
                                            {coupon.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Revoke Reason */}
                        {coupon.status === "REVOKED" && coupon.revokeReason && (
                            <Grid item xs={12}>
                                <Card sx={{ boxShadow: 2, border: "2px solid #f44336" }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{ color: "error.main", fontWeight: "bold", mb: 2 }}
                                        >
                                            🚫 Revoke Reason
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                lineHeight: 1.6,
                                                p: 2,
                                                backgroundColor: "#ffebee",
                                                borderRadius: 2,
                                                border: "1px solid #f44336",
                                                color: "#c62828",
                                                fontWeight: "medium",
                                            }}
                                        >
                                            {coupon.revokeReason}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Coupon Codes */}
                        <Grid item xs={12}>
                            <Card sx={{ boxShadow: 2 }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{ color: "primary.main", fontWeight: "bold", mb: 2 }}
                                    >
                                        🎫 Coupon Codes ({coupon.codes?.length || 0})
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 1,
                                            p: 2,
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: 2,
                                            border: "1px solid #e0e0e0",
                                        }}
                                    >
                                        {displayedCodes && displayedCodes.length > 0 ? (
                                            displayedCodes.map((codeObj: any, index: number) => (
                                                <Chip
                                                    key={codeObj.id}
                                                    label={codeObj.code}
                                                    variant="filled"
                                                    size="medium"
                                                    color="primary"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        fontSize: "0.9rem",
                                                        "&:hover": { transform: "scale(1.05)" },
                                                        transition: "transform 0.2s",
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No codes available
                                            </Typography>
                                        )}
                                    </Box>

                                    {hasMoreCodes && (
                                        <Box sx={{ mt: 2, textAlign: "center" }}>
                                            <Button
                                                onClick={() => setShowAllCodes(!showAllCodes)}
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    color: "primary.main",
                                                    borderColor: "primary.main",
                                                    "&:hover": {
                                                        backgroundColor: "primary.main",
                                                        color: "white",
                                                    },
                                                }}
                                            >
                                                {showAllCodes
                                                    ? `Show Less (${MAX_CODES_TO_SHOW})`
                                                    : `Show More (${coupon.codes?.length - MAX_CODES_TO_SHOW} more)`}
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </DashboardContent>

            <RevokeModal
                open={showRevokeModal}
                onClose={() => setShowRevokeModal(false)}
                onConfirm={handleRevoke}
                loading={isRevoking}
            />

            <ConfirmationModal
                open={showToggleModal}
                handleClose={() => setShowToggleModal(false)}
                onConfirm={handleToggle}
                title="Toggle Coupon Status"
                description={`Are you sure you want to ${coupon?.status === "ACTIVE" ? "deactivate" : "activate"} this coupon?`}
                loading={isToggling}
            />
        </>
    );
}
